import React, { useEffect, useRef, useState, type FC } from 'react';
import { Textarea, Space, Text, InputText, Select } from '@g2u/ui-kit';
import { Form, FormInstance, Spin } from 'antd';
import cx from 'classnames';

import type { EmailTemplate, JsonDataItem, SmsTemplate, WithOptionalDataTest } from 'types';
import styles from '../callbacks.module.scss';

export namespace CloseCallbackForm {
  export type Props = {
    form: FormInstance;
    step?: number;
    record: JsonDataItem;
    emailTemplates: EmailTemplate[];
    smsTemplates: SmsTemplate[];
  } & WithOptionalDataTest;

  export const $: FC<Props> = (props) => {
    const { step, record, form, emailTemplates, smsTemplates } = props;
    const [currentEmailTemplate, setCurrentEmailTemplate] = useState<string>();
    const [currentSmsTemplate, setCurrentSmsTemplate] = useState<string>();
    const emailMessageRef = useRef<HTMLDivElement>(null);
    const smsMessageRef = useRef<HTMLDivElement>(null);
    Form.useWatch([], { form });

    const commentStep = step === 0;
    const voiceMessageStep = step === 1;
    const emailStep = step === 2;
    const smsStep = step === 3;

    useEffect(() => {
      // send email
      if (emailMessageRef.current && emailStep) {
        const templateData = emailTemplates?.find((template) => template.ID.toString() === currentEmailTemplate);
        form.setFieldValue('subject', templateData?.subject);
        form.setFieldValue('message', templateData?.body);
        emailMessageRef.current.innerHTML =
          emailTemplates.find((template) => template.ID.toString() === currentEmailTemplate)?.body ?? '';
      }

      // send sms
      if (smsMessageRef.current && smsStep) {
        const templateData = smsTemplates?.find((template) => template.ID.toString() === currentSmsTemplate);
        form.setFieldValue('message', templateData?.message);
        smsMessageRef.current.innerHTML =
          smsTemplates.find((template) => template.ID.toString() === currentSmsTemplate)?.message ?? '';
      }
    }, [currentEmailTemplate, currentSmsTemplate]);

    if (step === undefined || isNaN(step)) return <Spin />;

    return (
      <Space.$ direction={Space.Direction.Vertical} size={Space.Size.Middle}>
        {commentStep && (
          <Textarea.$
            required
            name="callbackNote"
            dataTestId="callback-note"
            placeholder="Enter note"
            style={{ width: '100%', height: 200 }}
            rules={[{ required: true, message: 'Note is required' }]}
          />
        )}
        {voiceMessageStep && (
          <Space.$ direction={Space.Direction.Vertical} size={Space.Size.Middle}>
            <InputText.$
              required
              label="To Name:"
              name="name"
              disabled
              initialValue={record.task_links?.customer?.name.full_name}
            />
            <InputText.$
              required
              label="To Mobile:"
              name="mobile"
              disabled
              initialValue={record.task_links?.customer?.contact[0]}
            />
          </Space.$>
        )}
        {emailStep && (
          <Space.$ direction={Space.Direction.Vertical} size={Space.Size.Middle}>
            <InputText.$
              required
              label="To Name:"
              name="name"
              initialValue={record.task_links?.customer?.name.full_name}
            />
            <InputText.$ required label="To Email:" name="email" initialValue={record.task_links?.customer?.email} />
            <InputText.$ required label="Subject:" name="subject" initialValue={emailTemplates[0]?.subject} />
            <Select.$
              required
              label="Template:"
              name="template"
              options={emailTemplates.map((template) => {
                return {
                  label: <span className={styles.selectOption}>{template.name}</span>,
                  value: template.ID.toString(),
                };
              })}
              onChange={(id) => setCurrentEmailTemplate(id)}
            />
            {currentEmailTemplate && (
              <div>
                <div
                  ref={emailMessageRef}
                  contentEditable={true}
                  className={cx(
                    styles.callbacks__modalContenteditableDiv,
                    currentEmailTemplate &&
                      !form.getFieldValue('message') &&
                      styles.callbacks__modalContenteditableDiv_error,
                  )}
                  onInput={(e: any) => {
                    form.setFieldValue('message', e.target.innerHTML);
                  }}
                />
                <div>
                  {!form.getFieldValue('message') && (
                    <Text.$ type={'danger'} size={Text.Size.MD}>
                      Email is required
                    </Text.$>
                  )}
                </div>
              </div>
            )}
            <div style={{ height: 0, overflow: 'hidden' }}>
              <Textarea.$
                required
                hidden
                name="message"
                style={{ minHeight: 0, height: 0, marginTop: '-16px' }}
                rules={[{ required: true }]}
              />
            </div>
          </Space.$>
        )}
        {smsStep && (
          <Space.$ direction={Space.Direction.Vertical} size={Space.Size.Middle}>
            <InputText.$
              required
              label="To Name:"
              name="sms_to"
              initialValue={record.task_links?.customer?.name.full_name}
            />
            <InputText.$
              required
              label="To Mobile:"
              name="to_number"
              initialValue={record.task_links?.customer?.contact[0]}
            />
            <Select.$
              required
              label="Template:"
              name="template"
              options={smsTemplates.map((template) => {
                return {
                  label: <span className={styles.selectOption}>{template.name}</span>,
                  value: template.ID.toString(),
                };
              })}
              onChange={(id) => setCurrentSmsTemplate(id)}
            />
            {currentSmsTemplate && (
              <div>
                <div
                  ref={smsMessageRef}
                  contentEditable={true}
                  className={cx(
                    styles.callbacks__modalContenteditableDiv,
                    currentSmsTemplate &&
                      !form.getFieldValue('message') &&
                      styles.callbacks__modalContenteditableDiv_error,
                  )}
                  onInput={(e: any) => {
                    form.setFieldValue('message', e.target.innerHTML);
                  }}
                />
                <div>
                  {!form.getFieldValue('message') && (
                    <Text.$ type={'danger'} size={Text.Size.MD}>
                      SMS text is required
                    </Text.$>
                  )}
                </div>
              </div>
            )}
            <div style={{ height: 0, overflow: 'hidden' }}>
              <Textarea.$
                required
                hidden
                name="message"
                placeholder="Enter mobile"
                style={{ minHeight: 0, height: 0, marginTop: '-16px' }}
                rules={[{ required: true }]}
              />
            </div>
          </Space.$>
        )}
      </Space.$>
    );
  };
}
